import { useAppDispatch } from '@hooks/generic';
import { useDebounce } from '@hooks/useDebounce';
import {
    DropdownFilters,
    InboxDropdownFilterValues,
    InboxFilters,
    InboxManagementTable,
} from '@inbox/components/display';
import { InboxContextProvider } from '@inbox/contexts';
import { useGetAllInboxesQuery } from '@inbox/services/inbox';
import { IInbox } from '@inbox/types';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, SelectChangeEvent, Typography } from '@mui/material';
import { setTitle } from '@store/appSlice';
import { safeParseNumber } from '@utils/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InboxCTA } from '.';

enum GeneralFilters {
    PAGE = 'page',
    ROWS = 'rows',
    SEARCH = 'search',
    ORDER = 'ordering',
}

const InboxList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();

    const generalFilterValues = useMemo(
        () => ({
            page: safeParseNumber(searchParams.get(GeneralFilters.PAGE), 1),
            search: searchParams.get(GeneralFilters.SEARCH) ?? '',
            rows: safeParseNumber(searchParams.get(GeneralFilters.ROWS), 10),
            order: searchParams.get(GeneralFilters.ORDER) ?? 'email',
        }),
        [searchParams]
    );

    const [searchInput, setSearchInput] = useState(generalFilterValues.search);

    const getDropdownFilterValues = useCallback(
        (): InboxDropdownFilterValues => ({
            [DropdownFilters.COUNTRY]: searchParams.get(DropdownFilters.COUNTRY)?.split(',') ?? [],
            [DropdownFilters.IS_ACTIVE]: searchParams.get(DropdownFilters.IS_ACTIVE) ?? '',
        }),
        [searchParams]
    );

    const dropdownFilterValues = useMemo(getDropdownFilterValues, [getDropdownFilterValues]);

    const { data: inboxDataResponse, isLoading } = useGetAllInboxesQuery({
        page_size: generalFilterValues.rows,
        page: generalFilterValues.page,
        search: generalFilterValues.search,
        ordering: generalFilterValues.order,
        [DropdownFilters.COUNTRY]: dropdownFilterValues[DropdownFilters.COUNTRY] ?? [],
        [DropdownFilters.IS_ACTIVE]: dropdownFilterValues[DropdownFilters.IS_ACTIVE],
    });

    const { inboxes, querysetIds, count } = useMemo(
        () =>
            inboxDataResponse
                ? {
                      inboxes: inboxDataResponse.results,
                      count: inboxDataResponse.count,
                      querysetIds: inboxDataResponse.queryset_ids,
                  }
                : { inboxes: [], count: 0, querysetIds: [] },
        [inboxDataResponse]
    );

    const ordering = useMemo(() => {
        const order = generalFilterValues.order;
        if (order.startsWith('-')) {
            return { column: order.slice(1), direction: 'desc' } as {
                column: keyof Omit<IInbox, 'id' | 'created_at'>;
                direction: 'desc' | 'asc';
            };
        }
        return { column: order, direction: 'asc' as const } as {
            column: keyof Omit<IInbox, 'id' | 'created_at'>;
            direction: 'desc' | 'asc';
        };
    }, [generalFilterValues.order]);

    const handleParamChange = useCallback(
        ({ field, value, isDelete }: { field: string; value: string | string[] | null; isDelete?: boolean }) => {
            if (isDelete) {
                const values = searchParams.get(field);

                if (values?.split(',').length === 1) {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.set('page', '1');
                        params.delete(field);
                        return params;
                    });
                } else {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.set('page', '1');
                        params.set(
                            field,
                            values
                                ?.split(',')
                                .filter((x) => x !== value)
                                .join(',') ?? ''
                        );
                        return params;
                    });
                }
            } else if (searchParams.get(field) !== value) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    if (value) {
                        params.set(field, typeof value === 'string' ? value : value.join(','));
                    } else {
                        params.delete(field);
                    }
                    return params;
                });
            }
        },
        [searchParams, setSearchParams]
    );
    const handleSelectClick = (event: SelectChangeEvent<string | string[]>) => {
        const {
            target: { value, name },
        } = event;
        handleParamChange({ field: name, value: Array.isArray(value) ? value.join(',') : value });
    };

    const handleFilterReset = useCallback(() => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);

            params.set('page', '1');
            Object.values(DropdownFilters).forEach((filter) => params.delete(filter));
            return params;
        });
    }, [setSearchParams]);

    const handlePageChange = (newPage: number) =>
        handleParamChange({ field: GeneralFilters.PAGE, value: newPage.toString() });

    const handleRowsPerPageChange = (newRowsPerPage: number) =>
        handleParamChange({ field: GeneralFilters.ROWS, value: newRowsPerPage.toString() });

    const handleSearchChange = (newSearch: string) =>
        handleParamChange({ field: GeneralFilters.SEARCH, value: newSearch });

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
        setSearchInput(e.target.value);
    };

    const handleOrderChange = (column: keyof IInbox) => {
        const currentOrder = searchParams.get(GeneralFilters.ORDER);
        const newOrder = currentOrder === column ? `-${column}` : column;
        handleParamChange({ field: GeneralFilters.ORDER, value: newOrder });
    };

    const handleSearchClear = () => {
        setSearchInput('');
        handleSearchChange('');
    };

    useDebounce({
        value: searchInput,
        delay: 500,
        customHandler: handleSearchChange,
        trigger: () => generalFilterValues.search !== searchInput,
    });

    useEffect(() => {
        dispatch(setTitle('Inbox Management'));
    }, [dispatch]);

    return (
        <InboxContextProvider>
            <InboxCTA />
            <Box className="px-5 pt-9 pb-4 bg-white rounded-xl border border-neutral-200">
                <InboxFilters
                    activeFilters={dropdownFilterValues}
                    searchInput={searchInput}
                    onSelectClick={handleSelectClick}
                    onParamChange={handleParamChange}
                    onFilterReset={handleFilterReset}
                    onSearchChange={handleSearchInputChange}
                    onSearchClear={handleSearchClear}
                />
                <InboxManagementTable
                    data={inboxes}
                    count={count}
                    isLoading={isLoading}
                    querysetIds={querysetIds}
                    page={generalFilterValues.page}
                    onPageChange={handlePageChange}
                    rowsPerPage={generalFilterValues.rows}
                    setRowsPerPage={handleRowsPerPageChange}
                    onSortClick={handleOrderChange}
                    sort={ordering}
                />
            </Box>
        </InboxContextProvider>
    );
};

export default InboxList;
